import storage from "@/services/storage";
import { VUE_GOOGLEMAP_API_KEY } from "@/data/constants";

export interface SmartMatchConfig {
  id: string;
  smart_match_enabled: boolean;
  availability_enabled: boolean;
  exclude_do_not_use_enabled: boolean;
  required_skills_enabled: boolean;
  location_radius_enabled: boolean;
  location_radius_value: number;
  location_radius_unit: string;
  preferred_language_enabled: boolean;
  preferred_language_proficiency: number;
  soft_skills_enabled: boolean;
  title_type_match_enabled: boolean;
  previous_engagements_enabled: boolean;
  previous_engagements_min: number;
  transportation_enabled: boolean;
  rating_enabled: boolean;
  rating_min_stars: number;
  favourited_resource_enabled: boolean;
  hours_available_enabled: boolean;
  hours_available_min: number;
  smart_match_config_transportations: { transportation_type: string }[];
  smart_match_criteria_orders: { criterion_type: string; sort_order: number }[];
}

export interface Project {
  id: string;
  firstname: string;
  lastname: string;
  support_type: string;
  preferred_language: string;
  address?: string;
  project_skills: { skill: { id: string; name: string } }[];
  project_soft_skills: { soft_skill: { id: string; name: string } }[];
}

export interface ContractorResult {
  id: string;
  user_id: string;
  availability: boolean;
  user: {
    id: string;
    given_name: string;
    family_name: string;
    email: string;
    avatar?: string;
    favorited_by_users?: { id: string; favorited_by_id: string }[];
    certifications?: { title: string }[];
    profile?: {
      user_id: string;
      occupation?: string;
      summary?: string;
      min_rate?: number;
      max_rate?: number;
      currency?: string;
      rate_unit?: string;
      max_available_hours?: number;
      DNU_flag?: boolean;
      address?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
      skills: { skill: { id: string; name: string } }[];
      user_soft_skills: { soft_skill: { id: string; name: string } }[];
      languages: {
        language: { id: string; name: string; code: string };
        proficiency_level: number;
      }[];
      transportation: { transportation_type: string }[];
    };
  };
  contractor_ratings: { score: number; created_at: string }[];
  projects: { id: string; created_at: string }[];
  projects_aggregate: { aggregate: { count: number } };
  project_activities: {
    id: string;
    project_id: string;
    created_at: string;
    project: { title: string };
  }[];
}

export interface SmartMatchResult {
  id: string;
  userId: string;
  name: string;
  title: string;
  rating: number;
  matchPercentage: number;
  hourlyRate: string;
  avatar: string;
  favorited: boolean;
  favoriteId?: string;
  bookmarked: boolean;
  languages: string[];
  distance: string;
  hasVehicle: boolean;
  availableHours: number;
  clientWorkCount: number;
  credentials: string[];
  skills: string[];
  softSkills: string[];
  certifications: string[];
}

export class SmartMatchService {
  /**
   * Main entry point for calculating a contractor's match percentage against a project.
   * Uses a two-tier scoring system:
   * - Must-haves: All required criteria must pass (availability, skills, distance, etc.)
   * - Nice-to-haves: Weighted criteria that add bonus points (soft skills, rating, previous work, etc.)
   *
   * Scoring Formula:
   * - If must-haves pass: 50% base + 30% from skills match + 20% from nice-to-haves = up to 100%
   * - If must-haves fail: only nice-to-have score, capped at 49%
   *
   * Special handling for new clients:
   * - For new clients, 4 categories (availability, location_radius, previous_engagements, transportation) are disabled and receive full points
   *
   * Special handling for virtual visits:
   * - For virtual visits, location/distance and transportation criteria are bypassed (not relevant for remote work)
   *
   * @param contractor - Contractor data from database
   * @param project - Project/client requirements
   * @param config - Manager's Smart Match configuration settings
   * @param isNewClient - Whether this is for a new client (disables certain criteria)
   * @param isVirtual - Whether this is a virtual visit (disables location-based criteria)
   * @returns Promise<number> - Match percentage (0-100)
   */
  static async calculateMatchPercentage(
    contractor: ContractorResult,
    project: Project,
    config: SmartMatchConfig,
    isNewClient = false,
    isVirtual = false
  ): Promise<number> {
    const contractorName = `${contractor.user.given_name} ${contractor.user.family_name}`.trim();
    
    // Exit early if Smart Match is disabled for this manager
    if (!config.smart_match_enabled) {
      console.log(`❌ Smart Match disabled for this manager`);
      return 0;
    }

    // Step 1: Evaluate all must-have requirements (async due to distance calculations)
    const mustHavesMet = await this.checkMustHaveRequirements(
      contractor,
      project,
      config,
      isNewClient,
      isVirtual
    );

    // Step 2: Calculate weighted score from nice-to-have criteria (async due to distance scoring)
    const niceToHaveScore = await this.calculateNiceToHaveScore(
      contractor,
      project,
      config,
      isNewClient,
      isVirtual
    );

    // Step 3: Calculate proportional skills contribution if enabled
    let skillsContribution = 0;
    if (config.required_skills_enabled && project.project_skills.length > 0) {
      skillsContribution = this.calculateSkillsMatchScore(contractor, project, 100);
    }

    // Step 4: Apply proportional scoring based on enabled categories only
    const enabledCategories = this.getEnabledCategories(config, project, isNewClient);
    const score = this.calculateProportionalScore(
      mustHavesMet, 
      skillsContribution, 
      niceToHaveScore, 
      enabledCategories,
      config
    );

    //this.logFinalScoring(mustHavesMet, skillsContribution, niceToHaveScore, enabledCategories, score, config);
    console.log(`Percentage overview for ${contractorName}: \n`, {
      mustHavesMet,
      skillsContribution,
      niceToHaveScore,
      enabledCategories,
      score,
      config
    });
    return score;
  }

  /**
   * Determines which scoring categories are enabled based on configuration.
   * Used to calculate proportional scoring based only on active criteria.
   * 
   * @param config - Smart Match configuration
   * @param project - Project data
   * @param isNewClient - Whether this is for a new client
   * @returns object with enabled category flags
   */
  private static getEnabledCategories(config: SmartMatchConfig, project: Project, isNewClient: boolean) {
    // Check must-haves (excluding skills which is handled separately)
    const mustHavesEnabled = [
      config.availability_enabled && !isNewClient,
      config.exclude_do_not_use_enabled,
      config.preferred_language_enabled,
      config.location_radius_enabled && !isNewClient
    ].some(enabled => enabled);

    // Check if skills are enabled as a must-have
    const skillsEnabled = config.required_skills_enabled && project.project_skills.length > 0;

    // Check nice-to-haves
    const niceToHavesEnabled = [
      config.soft_skills_enabled,
      config.title_type_match_enabled,
      config.previous_engagements_enabled && !isNewClient,
      config.transportation_enabled && !isNewClient,
      config.rating_enabled,
      config.favourited_resource_enabled,
      config.hours_available_enabled,
      config.location_radius_enabled // Distance as nice-to-have
    ].some(enabled => enabled);

    return {
      mustHaves: mustHavesEnabled,
      skills: skillsEnabled,
      niceToHaves: niceToHavesEnabled
    };
  }

  /**
   * Calculates final score using the specified scoring formula.
   * 
   * Scoring Logic:
   * - Must-haves met: Score = ((matched_weights) / 100) × 49 + 51
   * - Must-haves failed: Score = ((matched_weights) / 100) × 49
   * - Resources with all must-haves get minimum 51% baseline
   * - Nice-to-haves add up to 49% bonus points based on weighted matches
   * 
   * @param mustHavesMet - Whether must-haves passed
   * @param skillsContribution - Skills match score (0-100) - treated as nice-to-have
   * @param niceToHaveScore - Sum of matched nice-to-have weights (0-100)
   * @param enabledCategories - Which categories are enabled
   * @param config - Configuration for skills check
   * @returns Final match percentage
   */
  private static calculateProportionalScore(
    mustHavesMet: boolean,
    skillsContribution: number,
    niceToHaveScore: number,
    enabledCategories: { mustHaves: boolean, skills: boolean, niceToHaves: boolean },
    config: SmartMatchConfig
  ): number {
    // If no categories enabled, return 0
    if (!enabledCategories.mustHaves && !enabledCategories.skills && !enabledCategories.niceToHaves) {
      return 0;
    }

    // Combine skills and nice-to-haves as they're both weighted bonus criteria
    let totalNiceToHaveScore = niceToHaveScore;
    
    // Add skills contribution if enabled (skills are treated as nice-to-have bonus)
    if (enabledCategories.skills && config.required_skills_enabled) {
      // Skills get a proportional weight in the nice-to-have calculation
      // This needs to be integrated with the nice-to-have weight system
      totalNiceToHaveScore += skillsContribution;
    }

    // Apply the specified scoring formula
    let finalScore: number;
    
    if (mustHavesMet && enabledCategories.mustHaves) {
      // Must-haves passed: Score = ((matched_weights) / 100) × 49 + 51
      finalScore = ((totalNiceToHaveScore / 100) * 49) + 51;
    } else if (enabledCategories.mustHaves) {
      // Must-haves failed: Score = ((matched_weights) / 100) × 49
      finalScore = (totalNiceToHaveScore / 100) * 49;
    } else {
      // Only nice-to-haves enabled - use the formula but without must-have baseline
      // This handles the edge case where no must-haves are configured
      finalScore = ((totalNiceToHaveScore / 100) * 49) + 51;
    }

    return Math.round(Math.min(finalScore, 100));
  }




  /**
   * Validates that a contractor meets all enabled must-have requirements.
   * Must-haves are hard requirements - if any fail, the contractor cannot achieve >49% match.
   *
   * Checks performed (when enabled in config):
   * - Availability: Is contractor marked as available? (disabled for new clients)
   * - Do Not Use: Is contractor flagged as DNU (excluded)?
   * - Required Skills: Does contractor have at least one project-required skill? (degree of match scored proportionally in nice-to-haves)
   * - Language: Does contractor speak the project's preferred language at required proficiency?
   * - Distance: Is contractor within the specified radius of project location? (disabled for new clients and virtual visits)
   *
   * For new clients, availability and location_radius checks are bypassed (auto-pass).
   * For virtual visits, location_radius checks are bypassed (location not relevant for remote work).
   *
   * @param contractor - Contractor data to evaluate
   * @param project - Project requirements
   * @param config - Manager's criteria configuration
   * @param isNewClient - Whether this is for a new client (disables certain criteria)
   * @param isVirtual - Whether this is a virtual visit (disables location-based criteria)
   * @returns Promise<boolean> - True if all enabled must-haves pass
   */
  private static async checkMustHaveRequirements(
    contractor: ContractorResult,
    project: Project,
    config: SmartMatchConfig,
    isNewClient = false,
    isVirtual = false
  ): Promise<boolean> {
    // Check contractor availability status (bypassed for new clients)
    if (config.availability_enabled && !isNewClient) {
      if (!contractor.availability) {
        return false;
      }
    }

    // Check Do Not Use flag - exclude flagged contractors
    if (config.exclude_do_not_use_enabled) {
      const dnuFlag = contractor.user.profile?.DNU_flag;
      if (dnuFlag) {
        return false;
      }
    }

    // Verify contractor has at least some required technical skills (proportional scoring will handle the degree of match)
    if (config.required_skills_enabled && project.project_skills.length > 0) {
      const contractorSkillIds = contractor.user.profile?.skills?.map((us) => us.skill.id) || [];
      const projectSkillIds = project.project_skills.map((ps) => ps.skill.id);
      const hasAnyMatchingSkill = projectSkillIds.some((skillId) => contractorSkillIds.includes(skillId));
      
      // Only fail if contractor has absolutely no matching skills
      if (!hasAnyMatchingSkill) {
        return false;
      }
    }

    // Check language proficiency requirements
    if (config.preferred_language_enabled && project.preferred_language) {
      const hasLanguage = this.hasRequiredLanguage(
        contractor,
        project,
        config.preferred_language_proficiency
      );
      if (!hasLanguage) {
        return false;
      }
    }

    // Check geographic distance constraints (async due to geocoding) (bypassed for new clients and virtual visits)
    if (config.location_radius_enabled && project.address && !isNewClient && !isVirtual) {
      const isWithinRadius = await this.isWithinLocationRadius(
        contractor,
        project,
        config.location_radius_value,
        config.location_radius_unit
      );
      if (!isWithinRadius) {
        return false;
      }
    }

    // All enabled must-haves passed
    return true;
  }

  /**
   * Calculates weighted score from nice-to-have criteria that add bonus points to the match.
   * Nice-to-haves are preferences that improve the match but don't disqualify contractors.
   *
   * Process:
   * 1. Filter enabled criteria from manager's configuration
   * 2. Sort by priority order (sort_order field)
   * 3. Redistribute base weights [25, 20, 18, 15, 12, 10, 5] proportionally
   * 4. Score each criterion (proportional for skills/soft skills, binary pass/fail for others, graduated for distance/engagements)
   * 5. Sum weighted scores to get total nice-to-have score
   *
   * Available criteria:
   * - required_skills: Contractor has project's required technical skills (proportional scoring)
   * - soft_skills: Contractor has project's preferred soft skills (proportional scoring)
   * - title_type_match: Contractor's occupation matches project support type
   * - previous_engagements: Contractor has minimum number of past projects
   * - transportation: Contractor has required transportation methods (skipped for virtual visits)
   * - rating: Contractor meets minimum average rating threshold
   * - favourited_resource: Contractor is favorited by current manager
   * - hours_available: Contractor has minimum available hours per week
   * - distance: Proximity bonus (graduated scoring, closer = higher score, skipped for virtual visits)
   *
   * @param contractor - Contractor to evaluate
   * @param project - Project requirements
   * @param config - Manager's weighted criteria configuration
   * @param isNewClient - Whether this is for a new client (gives full points for certain criteria)
   * @param isVirtual - Whether this is a virtual visit (skips location-based criteria)
   * @returns Promise<number> - Total nice-to-have score (0-100)
   */
  private static async calculateNiceToHaveScore(
    contractor: ContractorResult,
    project: Project,
    config: SmartMatchConfig,
    isNewClient = false,
    isVirtual = false
  ): Promise<number> {
    // Get list of enabled nice-to-have criteria based on manager's configuration
    const enabledCriteria = config.smart_match_criteria_orders
      .filter((criteria) => {
        switch (criteria.criterion_type) {
          case "soft_skills":
            return config.soft_skills_enabled;
          case "title_type_match":
            return config.title_type_match_enabled;
          case "previous_engagements":
            return config.previous_engagements_enabled;
          case "transportation":
            // Skip transportation for virtual visits (not relevant for remote work)
            return config.transportation_enabled && !isVirtual;
          case "rating":
            return config.rating_enabled;
          case "favourited_resource":
            return config.favourited_resource_enabled;
          case "hours_available":
            return config.hours_available_enabled;
          case "distance":
            // Skip distance for virtual visits (physical location doesn't matter)
            return config.location_radius_enabled && !isVirtual;
          default:
            return false;
        }
      })
      .sort((a, b) => a.sort_order - b.sort_order); // Apply manager's priority ordering

    // If no nice-to-haves are enabled, give full points
    if (enabledCriteria.length === 0) {
      return 100;
    }

    // Redistribute base weights proportionally based on number of enabled criteria
    const baseWeights = [25, 20, 15, 12, 10, 10, 8];
    const weights = this.redistributeWeights(
      baseWeights,
      enabledCriteria.length
    );

    let totalScore = 0;

    // Evaluate each enabled criterion and apply weighted scoring
    for (let index = 0; index < enabledCriteria.length; index++) {
      const criteria = enabledCriteria[index];
      const weight = weights[index] || 0;
      let passed = false;
      let criteriaScore = 0;
      let details = "";

      switch (criteria.criterion_type) {
        case "soft_skills":
          // Use proportional scoring for soft skills
          criteriaScore = this.calculateSoftSkillsMatchScore(contractor, project, weight);
          details = `(proportional: ${(criteriaScore/weight*100).toFixed(1)}% match)`;
          break;
        case "title_type_match":
          const contractorJobTitle = this.getContractorJobTitle(contractor.user.profile);
          const projectSupportTypes = this.parseProjectSupportTypes(project.support_type);
          passed = this.checkTitleTypeMatch(contractor, project);
          criteriaScore = passed ? weight : 0;
          details = `"${contractorJobTitle}" vs [${projectSupportTypes.join(', ')}] → ${passed ? '✅ MATCH' : '❌ NO MATCH'}`;
          break;
        case "previous_engagements":
          // Use incremental scoring based on engagement ranges (full points for new clients)
          if (isNewClient) {
            criteriaScore = weight; // Full points for new clients
            details = `(new client - auto full points)`;
          } else {
            const engagementCount = contractor.projects_aggregate?.aggregate?.count || 0;
            criteriaScore = this.calculatePreviousEngagementsScore(
              contractor,
              config.previous_engagements_min,
              weight
            );
            details = `(${engagementCount} engagements, min: ${config.previous_engagements_min})`;
          }
          break;
        case "transportation":
          // Check transportation requirements (full points for new clients)
          if (isNewClient) {
            criteriaScore = weight; // Full points for new clients
            details = `(new client - auto full points)`;
          } else {
            passed = this.checkTransportationMatch(contractor, config);
            criteriaScore = passed ? weight : 0;
            details = `${passed ? '✅ HAS' : '❌ MISSING'} required transport`;
          }
          break;
        case "rating":
          const avgRating = contractor.contractor_ratings.length > 0 
            ? this.calculateAverageRating(contractor.contractor_ratings) 
            : 0;
          passed = this.checkRatingRequirement(
            contractor,
            config.rating_min_stars
          );
          criteriaScore = passed ? weight : 0;
          details = `(${avgRating.toFixed(1)}⭐ vs ${config.rating_min_stars}⭐ min)`;
          break;
        case "favourited_resource":
          passed = this.checkFavouritedResource(contractor);
          criteriaScore = passed ? weight : 0;
          details = passed ? '💖 favorited' : 'not favorited';
          break;
        case "hours_available":
          const availableHours = contractor.user.profile?.max_available_hours || 0;
          passed = this.checkHoursAvailable(
            contractor,
            config.hours_available_min
          );
          criteriaScore = passed ? weight : 0;
          details = `(${availableHours}h available vs ${config.hours_available_min}h min)`;
          break;
        case "distance":
          // Distance uses graduated scoring - closer contractors get more points
          criteriaScore = await this.calculateDistanceScore(
            contractor,
            project,
            config,
            weight
          );
          details = `(graduated scoring: ${(criteriaScore/weight*100).toFixed(1)}% of max)`;
          break;
        default:
          criteriaScore = 0;
          details = 'unknown criterion';
      }

      totalScore += criteriaScore;
    }

    // Return the sum of matched weights (not percentage)
    // This aligns with the specification where the score formula expects raw weight sum
    return totalScore;
  }

  /**
   * Redistributes base weights proportionally when fewer criteria are enabled than the maximum.
   * Ensures the total weight always sums to 100, regardless of how many criteria are active.
   * 
   * Example:
   * - Base weights: [25, 20, 18, 15, 12, 10, 5] (total: 105)
   * - If only 3 criteria enabled: takes [25, 20, 18] (total: 63)
   * - Redistributes: [39.7, 31.7, 28.6] (total: 100)
   * 
   * @param baseWeights - Array of base weights in priority order
   * @param enabledCount - Number of criteria actually enabled
   * @returns number[] - Redistributed weights that sum to 100
   */
  private static redistributeWeights(
    baseWeights: number[],
    enabledCount: number
  ): number[] {
    if (enabledCount === 0) return [];

    // Take only the weights for enabled criteria (in priority order)
    const availableWeights = baseWeights.slice(0, enabledCount);
    const totalWeight = availableWeights.reduce(
      (sum, weight) => sum + weight,
      0
    );

    // Scale weights proportionally to sum to 100
    const redistributed = availableWeights.map(
      (weight) => (weight / totalWeight) * 100
    );

    return redistributed;
  }

  /**
   * Verifies contractor has at least some of the technical skills required by the project.
   * Uses skill IDs for exact matching to avoid false positives from similar skill names.
   * 
   * Logic:
   * - If project has no required skills, automatically passes
   * - If project has required skills, contractor must have at least one matching skill
   * - Uses 'some' rather than 'every' - partial skill match is sufficient
   * 
   * @param contractor - Contractor with skill profile
   * @param project - Project with required skills list
   * @returns boolean - True if contractor has sufficient skills
   */
  private static hasRequiredSkills(
    contractor: ContractorResult,
    project: Project
  ): boolean {
    // No skills required - automatic pass
    if (project.project_skills.length === 0) return true;

    // Extract skill IDs for comparison
    const contractorSkillIds =
      contractor.user.profile?.skills?.map((us) => us.skill.id) || [];
    const projectSkillIds = project.project_skills.map((ps) => ps.skill.id);
    
    // Contractor must have at least one required skill
    return projectSkillIds.some((skillId) =>
      contractorSkillIds.includes(skillId)
    );
  }

  /**
   * Calculates proportional score for skills matching based on percentage of required skills the contractor has.
   * 
   * Examples:
   * - Project requires: [skill-A, skill-B, skill-C, skill-D] (4 total)
   * - Contractor has: [skill-A] → 1/4 = 25% match
   * - Contractor has: [skill-A, skill-B, skill-C] → 3/4 = 75% match
   * - Contractor has: [skill-A, skill-B, skill-C, skill-D] → 4/4 = 100% match
   * 
   * @param contractor - Contractor with skill profile
   * @param project - Project with required skills list
   * @param maxWeight - Maximum points available for this criterion
   * @returns number - Proportional score (0 to maxWeight)
   */
  private static calculateSkillsMatchScore(
    contractor: ContractorResult,
    project: Project,
    maxWeight: number
  ): number {
    // No skills required - full points
    if (project.project_skills.length === 0) return maxWeight;

    // Extract skill IDs for comparison
    const contractorSkillIds =
      contractor.user.profile?.skills?.map((us) => us.skill.id) || [];
    const projectSkillIds = project.project_skills.map((ps) => ps.skill.id);

    // Count matching skills
    const matchingSkills = projectSkillIds.filter((skillId) =>
      contractorSkillIds.includes(skillId)
    );

    // Calculate proportional score
    const matchPercentage = matchingSkills.length / projectSkillIds.length;
    return maxWeight * matchPercentage;
  }

  /**
   * Checks if contractor speaks the project's preferred language at the required proficiency level.
   * Uses case-insensitive language name matching and proficiency level comparison.
   * 
   * Proficiency levels are numbered where lower = better (1 = fluent, 5 = basic)
   * So contractor's level must be <= required level to qualify.
   * 
   * @param contractor - Contractor with language skills
   * @param project - Project with preferred language requirement
   * @param minProficiency - Maximum proficiency level allowed (lower numbers = higher skill)
   * @returns boolean - True if contractor meets language requirements
   */
  private static hasRequiredLanguage(
    contractor: ContractorResult,
    project: Project,
    minProficiency: number
  ): boolean {
    // No language requirement - automatic pass
    if (!project.preferred_language) return true;

    const contractorLanguages = contractor.user.profile?.languages || [];
    
    // Check if contractor has the required language at sufficient proficiency
    return contractorLanguages.some(
      (ul) =>
        ul.language.name.toLowerCase() ===
          project.preferred_language.toLowerCase() &&
        ul.proficiency_level <= minProficiency // Lower number = higher proficiency
    );
  }

  /**
   * Evaluates contractor's soft skills against project preferences.
   * Soft skills are interpersonal abilities like communication, teamwork, problem-solving.
   * 
   * Scoring logic:
   * - If project specifies soft skills: contractor must have at least one match
   * - If project has no soft skill requirements: contractor just needs to have any soft skills listed
   * 
   * @param contractor - Contractor with soft skills profile
   * @param project - Project with preferred soft skills
   * @returns boolean - True if contractor meets soft skill criteria
   */
  private static checkSoftSkillsMatch(
    contractor: ContractorResult,
    project: Project
  ): boolean {
    // If project has no soft skill requirements, just verify contractor has some soft skills
    if (project.project_soft_skills.length === 0) {
      return (contractor.user.profile?.user_soft_skills?.length || 0) > 0;
    }

    // Extract soft skill IDs for comparison
    const contractorSoftSkillIds =
      contractor.user.profile?.user_soft_skills?.map(
        (uss) => uss.soft_skill.id
      ) || [];
    const projectSoftSkillIds = project.project_soft_skills.map(
      (pss) => pss.soft_skill.id
    );

    // Contractor must have at least one required soft skill
    return projectSoftSkillIds.some((skillId) =>
      contractorSoftSkillIds.includes(skillId)
    );
  }

  /**
   * Calculates proportional score for soft skills matching based on percentage of required soft skills the contractor has.
   * 
   * Examples:
   * - Project requires: [communication, teamwork, leadership, problem-solving] (4 total)
   * - Contractor has: [communication] → 1/4 = 25% match
   * - Contractor has: [communication, teamwork, leadership] → 3/4 = 75% match
   * - Contractor has: [communication, teamwork, leadership, problem-solving] → 4/4 = 100% match
   * 
   * @param contractor - Contractor with soft skills profile
   * @param project - Project with preferred soft skills
   * @param maxWeight - Maximum points available for this criterion
   * @returns number - Proportional score (0 to maxWeight)
   */
  private static calculateSoftSkillsMatchScore(
    contractor: ContractorResult,
    project: Project,
    maxWeight: number
  ): number {
    // If project has no soft skill requirements, give full points if contractor has any soft skills
    if (project.project_soft_skills.length === 0) {
      return (contractor.user.profile?.user_soft_skills?.length || 0) > 0 ? maxWeight : 0;
    }

    // Extract soft skill IDs for comparison
    const contractorSoftSkillIds =
      contractor.user.profile?.user_soft_skills?.map(
        (uss) => uss.soft_skill.id
      ) || [];
    const projectSoftSkillIds = project.project_soft_skills.map(
      (pss) => pss.soft_skill.id
    );

    // Count matching soft skills
    const matchingSoftSkills = projectSoftSkillIds.filter((skillId) =>
      contractorSoftSkillIds.includes(skillId)
    );

    // Calculate proportional score
    const matchPercentage = matchingSoftSkills.length / projectSoftSkillIds.length;
    return maxWeight * matchPercentage;
  }

  /**
   * Checks if contractor's job title exactly matches any of the project's required support types.
   * Compares contractor's job title from user_job_title table with client's support_type requirements.
   * 
   * Examples of matches:
   * - Contractor: "Personal Support Worker (PSW)" vs Project support_type: ["Personal Support Worker (PSW)", "Registered Nurse (RN)"] → match
   * - Contractor: "Registered Nurse (RN)" vs Project support_type: ["Personal Support Worker (PSW)"] → no match
   * - Contractor: "Housekeeping / Cleaning Services" vs Project support_type: ["Housekeeping / Cleaning Services"] → match
   * 
   * @param contractor - Contractor with job title from user_job_title system
   * @param project - Project with support_type array field
   * @returns boolean - True if contractor's job title exactly matches any required support type
   */
  private static checkTitleTypeMatch(
    contractor: ContractorResult,
    project: Project
  ): boolean {
    // Get contractor's job title from the new job title system (using type assertion)
    const profile = contractor.user.profile as any;
    let contractorJobTitle = "";
    
    // First check user_job_title (new system)
    if (profile?.user_job_title?.job_title) {
      contractorJobTitle = profile.user_job_title.job_title;
    }
    // Fallback to legacy job_title field
    else if (profile?.job_title) {
      contractorJobTitle = profile.job_title;
    }
    // Final fallback to occupation field (old system)
    else if (profile?.occupation) {
      contractorJobTitle = profile.occupation;
    }
    
    if (!contractorJobTitle) {
      return false;
    }
    
    // Parse project support_type (stored as PostgreSQL array string like {"value1","value2"})
    let projectSupportTypes: string[] = [];
    if (project.support_type) {
      try {
        // Handle PostgreSQL array format: {"value1","value2"} or JSON array format
        if (typeof project.support_type === 'string') {
          if (project.support_type.startsWith('{') && project.support_type.endsWith('}')) {
            // PostgreSQL array format: {"Personal Support Worker (PSW)","Registered Nurse (RN)"}
            const cleanedString = project.support_type.slice(1, -1); // Remove { }
            projectSupportTypes = cleanedString
              .split('","')
              .map(item => item.replace(/^"|"$/g, '')); // Remove quotes
          } else {
            // Single value or other string format
            projectSupportTypes = [project.support_type];
          }
        } else if (Array.isArray(project.support_type)) {
          // Already an array
          projectSupportTypes = project.support_type;
        }
      } catch (error) {
        console.log('Error parsing support_type:', error);
        return false;
      }
    }
    
    if (projectSupportTypes.length === 0) {
      return false;
    }
    
    // Check for exact match between contractor's job title and any required support type
    return projectSupportTypes.some(supportType => 
      contractorJobTitle.toLowerCase().trim() === supportType.toLowerCase().trim()
    );
  }

  /**
   * Verifies contractor has sufficient previous project experience.
   * Counts total number of projects contractor has worked on historically.
   * 
   * @param contractor - Contractor with project history aggregate
   * @param minEngagements - Minimum number of previous projects required
   * @returns boolean - True if contractor meets experience threshold
   */
  private static checkPreviousEngagements(
    contractor: ContractorResult,
    minEngagements: number
  ): boolean {
    const engagementCount = contractor.projects_aggregate.aggregate.count || 0;
    return engagementCount >= minEngagements;
  }

  /**
   * Calculates incremental score for previous engagements based on contractor's experience level.
   * Uses graduated scoring where contractors get more points for higher engagement counts.
   * 
   * Scoring Ranges:
   * - 0 engagements: 0% of weight
   * - 1-3 engagements: 25% of weight  
   * - 4-6 engagements: 50% of weight
   * - 7-9 engagements: 75% of weight
   * - 10+ engagements: 100% of weight
   * 
   * If minimum requirement is set and contractor meets/exceeds it, they get full points.
   * If no minimum set, graduated scoring applies based on engagement count.
   * 
   * @param contractor - Contractor with project history aggregate
   * @param minEngagements - Minimum requirement from configuration (0, 1, 4, 7, or 10)
   * @param maxWeight - Maximum points available for this criterion
   * @returns number - Calculated score (0 to maxWeight)
   */
  private static calculatePreviousEngagementsScore(
    contractor: ContractorResult,
    minEngagements: number,
    maxWeight: number
  ): number {
    const engagementCount = contractor.projects_aggregate.aggregate.count || 0;

    // If contractor meets or exceeds the minimum requirement, give full points
    if (engagementCount >= minEngagements && minEngagements > 0) {
      return maxWeight;
    }

    // Apply graduated scoring based on engagement count
    if (engagementCount === 0) {
      return 0;
    } else if (engagementCount >= 1 && engagementCount <= 3) {
      return maxWeight * 0.25; // 25% of weight
    } else if (engagementCount >= 4 && engagementCount <= 6) {
      return maxWeight * 0.5;  // 50% of weight
    } else if (engagementCount >= 7 && engagementCount <= 9) {
      return maxWeight * 0.75; // 75% of weight
    } else if (engagementCount >= 10) {
      return maxWeight; // 100% of weight
    }

    return 0;
  }

  /**
   * Checks if contractor has required transportation methods (car, public transit, etc.).
   * Important for roles requiring travel to client locations.
   * 
   * Logic:
   * - If no transportation requirements configured: automatic pass
   * - If requirements exist: contractor must have at least one matching method
   * - If contractor has no transportation listed: automatic fail
   * 
   * @param contractor - Contractor with transportation methods
   * @param config - Manager's transportation requirements
   * @returns boolean - True if contractor meets transportation needs
   */
  private static checkTransportationMatch(
    contractor: ContractorResult,
    config: SmartMatchConfig
  ): boolean {
    try {
      // No transportation requirements - automatic pass
      if (
        !config.smart_match_config_transportations ||
        config.smart_match_config_transportations.length === 0
      ) {
        return true;
      }

      const contractorTransportation =
        contractor.user.profile?.transportation?.map(
          (ut) => ut.transportation_type
        ) || [];
      const requiredTransportation =
        config.smart_match_config_transportations.map(
          (ct) => ct.transportation_type
        );

      // Contractor has no transportation methods - fail
      if (contractorTransportation.length === 0) {
        return false;
      }

      // Check for at least one matching transportation method
      return requiredTransportation.some((transport) =>
        contractorTransportation.includes(transport)
      );
    } catch (error) {
      console.log("Error in checkTransportationMatch:", error);
      return false;
    }
  }

  /**
   * Validates contractor meets minimum average rating threshold.
   * Only contractors with client feedback history are evaluated.
   * 
   * @param contractor - Contractor with rating history
   * @param minRating - Minimum average rating required (e.g., 4.0 stars)
   * @returns boolean - True if contractor's average rating meets threshold
   */
  private static checkRatingRequirement(
    contractor: ContractorResult,
    minRating: number
  ): boolean {
    // No ratings available - cannot meet rating requirement
    if (contractor.contractor_ratings.length === 0) {
      return false;
    }

    // Calculate average from all ratings and compare to threshold
    const averageRating = this.calculateAverageRating(
      contractor.contractor_ratings
    );
    return averageRating >= minRating;
  }

  /**
   * Calculates the average rating score from all contractor ratings.
   * Handles the mathematical average and rounds to 2 decimal places for consistency.
   * 
   * @param ratings - Array of rating objects with numeric scores
   * @returns number - Average rating rounded to 2 decimal places (0 if no ratings)
   */
  private static calculateAverageRating(
    ratings: { score: number; created_at: string }[]
  ): number {
    if (ratings.length === 0) return 0;

    // Sum all rating scores
    const totalScore = ratings.reduce((sum, rating) => sum + rating.score, 0);
    const average = totalScore / ratings.length;

    // Round to 2 decimal places for display consistency
    return Math.round(average * 100) / 100;
  }

  /**
   * Checks if contractor has been favorited by the current manager.
   * Favorited contractors get bonus points as they represent known good resources.
   * 
   * @param contractor - Contractor with favorited_by_users relationship data
   * @returns boolean - True if contractor is favorited by current manager
   */
  private static checkFavouritedResource(
    contractor: ContractorResult
  ): boolean {
    return (contractor.user.favorited_by_users?.length || 0) > 0;
  }

  /**
   * Verifies contractor has sufficient weekly availability for the project's needs.
   * Compares contractor's maximum available hours against manager's minimum requirement.
   * 
   * @param contractor - Contractor with availability profile
   * @param minHours - Minimum hours per week required
   * @returns boolean - True if contractor can work required hours
   */
  private static checkHoursAvailable(
    contractor: ContractorResult,
    minHours: number
  ): boolean {
    const maxAvailableHours = contractor.user.profile?.max_available_hours || 0;
    return maxAvailableHours >= minHours;
  }

  /**
   * Calculates graduated distance score for nice-to-have weighting.
   * Unlike must-have distance filtering (pass/fail), this provides bonus points based on proximity.
   * 
   * Scoring Algorithm:
   * - 0 km away: Full weight points
   * - At max radius: 0 points
   * - In between: Linear scaling (closer = more points)
   * - Beyond max radius: 0 points
   * 
   * Process:
   * 1. Build address strings from profile components
   * 2. Geocode both contractor and project addresses
   * 3. Calculate distance using Haversine formula
   * 4. Convert to configured unit (km/miles)
   * 5. Apply linear proximity scoring within radius
   * 
   * @param contractor - Contractor with location profile
   * @param project - Project with location address
   * @param config - Distance configuration (radius value/unit)
   * @param maxWeight - Maximum points available for this criterion
   * @returns Promise<number> - Distance score (0 to maxWeight)
   */
  private static async calculateDistanceScore(
    contractor: ContractorResult,
    project: Project,
    config: SmartMatchConfig,
    maxWeight: number
  ): Promise<number> {
    try {
      const profile = contractor.user.profile;
      if (!profile || !project.address) {
        return 0;
      }

      // Build contractor address from profile components
      const userAddressParts = [
        profile.address,
        profile.city,
        profile.state,
        profile.postal_code,
        profile.country
      ].filter(part => part && part.trim() !== '');
      
      const userAddressString = userAddressParts.join(', ');
      
      if (!userAddressString) {
        return 0;
      }

      // Geocode both addresses to get coordinates
      const userCoords = await this.geocodeAddress(userAddressString);
      const projectCoords = await this.geocodeAddress(project.address);

      if (!userCoords || !projectCoords) {
        return 0;
      }

      // Calculate distance using Haversine formula
      const distanceKm = this.haversineDistance(
        userCoords.lat,
        userCoords.lng,
        projectCoords.lat,
        projectCoords.lng
      );

      // Convert to configured unit if necessary
      let distance = distanceKm;
      const unit = config.location_radius_unit?.toLowerCase() || '';
      if (unit === 'miles' || unit === 'mi' || unit === 'mile') {
        distance = distanceKm * 0.621371; // Convert km to miles
      }

      const maxRadius = config.location_radius_value || 50;
      
      // Apply graduated scoring based on proximity
      if (distance <= maxRadius) {
        // Linear scoring: full points at 0 distance, scaling down to 0 at max radius
        const score = maxWeight * (1 - (distance / maxRadius));
        return Math.max(0, score);
      } else {
        // Beyond configured radius: no bonus points
        return 0;
      }
      
    } catch (error) {
      console.log('Error in calculateDistanceScore:', error);
      return 0;
    }
  }

  /**
   * Must-have requirement: checks if contractor is within the specified geographic radius.
   * This is a hard filter - contractors outside the radius are excluded entirely.
   * 
   * Process:
   * 1. Build address strings from contractor profile and project data
   * 2. Geocode addresses to get latitude/longitude coordinates
   * 3. Calculate precise distance using Haversine formula
   * 4. Convert distance to manager's preferred unit (km/miles)
   * 5. Compare against configured radius threshold
   * 
   * @param contractor - Contractor with location profile
   * @param project - Project with address
   * @param radiusValue - Maximum distance allowed (e.g., 25)
   * @param radiusUnit - Unit of measurement (e.g., "kilometres", "miles")
   * @returns Promise<boolean> - True if contractor is within radius
   */
  private static async isWithinLocationRadius(
    contractor: ContractorResult,
    project: Project,
    radiusValue: number,
    radiusUnit: string
  ): Promise<boolean> {
    try {
      const profile = contractor.user.profile;
      if (!profile || !project.address) {
        return false;
      }

      // Build contractor address from profile components
      const userAddressParts = [
        profile.address,
        profile.city,
        profile.state,
        profile.postal_code,
        profile.country
      ].filter(part => part && part.trim() !== '');
      
      const userAddressString = userAddressParts.join(', ');
      
      if (!userAddressString) {
        return false;
      }

      // Geocode both addresses to coordinates
      const userCoords = await this.geocodeAddress(userAddressString);
      const projectCoords = await this.geocodeAddress(project.address);

      if (!userCoords || !projectCoords) {
        return false;
      }

      // Calculate precise distance using Haversine formula
      const distanceKm = this.haversineDistance(
        userCoords.lat,
        userCoords.lng,
        projectCoords.lat,
        projectCoords.lng
      );

      // Convert to manager's preferred unit
      let distance = distanceKm;
      const unit = radiusUnit.toLowerCase();
      if (unit === 'miles' || unit === 'mi' || unit === 'mile') {
        distance = distanceKm * 0.621371; // Convert km to miles
      }
      
      // Return true if contractor is within the specified radius
      return distance <= radiusValue;
      
    } catch (error) {
      console.log('Error in isWithinLocationRadius:', error);
      return false;
    }
  }

  /**
   * Calculates human-readable distance string between contractor and project locations.
   * Used for displaying distance information in the UI.
   * 
   * Process:
   * 1. Builds full address strings from profile components
   * 2. Geocodes addresses to get precise coordinates
   * 3. Calculates distance using Haversine formula
   * 4. Converts to requested unit and formats for display
   * 
   * @param userProfile - Contractor's address components
   * @param projectAddress - Full project address string
   * @param unit - Preferred unit ('km', 'kilometres', 'miles', 'mi')
   * @returns Promise<string> - Formatted distance string (e.g., "12.5 km away")
   */
  static async calculateDistance(
    userProfile: {
      address?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    },
    projectAddress: string,
    unit: string = 'km'
  ): Promise<string> {
    try {
      // Build complete address string from profile components
      const userAddressParts = [
        userProfile.address,
        userProfile.city,
        userProfile.state,
        userProfile.postal_code,
        userProfile.country
      ].filter(part => part && part.trim() !== '');
      
      const userAddressString = userAddressParts.join(', ');
      
      if (!userAddressString || !projectAddress) {
        return 'Distance unavailable';
      }

      // Geocode addresses to get coordinates
      const userCoords = await this.geocodeAddress(userAddressString);
      const projectCoords = await this.geocodeAddress(projectAddress);

      if (!userCoords || !projectCoords) {
        return 'Distance unavailable';
      }

      // Calculate precise distance using spherical geometry
      const distanceKm = this.haversineDistance(
        userCoords.lat,
        userCoords.lng,
        projectCoords.lat,
        projectCoords.lng
      );

      // Convert to requested unit and set appropriate label
      let distance = distanceKm;
      let unitLabel = 'km';
      const unitLower = unit.toLowerCase();
      if (unitLower === 'miles' || unitLower === 'mi' || unitLower === 'mile') {
        distance = distanceKm * 0.621371; // km to miles conversion
        unitLabel = 'mi';
      } else if (unitLower === 'kilometres' || unitLower === 'kilometers' || unitLower === 'km') {
        unitLabel = 'km';
      }
      
      return `${distance.toFixed(1)} ${unitLabel} away`;
      
    } catch (error) {
      console.log('❌ Error calculating distance:', error);
      return 'Distance unavailable';
    }
  }

  /**
   * Converts street address to geographic coordinates using Google Maps Geocoding API.
   * Essential for accurate distance calculations between contractor and project locations.
   * 
   * @param address - Full street address string
   * @returns Promise<{lat: number, lng: number} | null> - Coordinates or null if geocoding fails
   */
  static async geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
    try {
      // URL encode address for safe API transmission
      const encodedAddress = encodeURIComponent(address);
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${VUE_GOOGLEMAP_API_KEY}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.log(`❌ HTTP error: ${response.status} ${response.statusText}`);
        return null;
      }
      
      const data = await response.json();
      
      // Parse successful geocoding response
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        const coords = { lat: location.lat, lng: location.lng };
        
        return coords;
      } else {
        // Log API errors for debugging
        if (data.error_message) {
          console.log(`   Error message: ${data.error_message}`);
        }
        return null;
      }
      
    } catch (error) {
      console.log(`❌ Error calling Google Maps Geocoding API for address: "${address}"`, error);
      return null;
    }
  }

  /**
   * Calculates the great-circle distance between two points on Earth using the Haversine formula.
   * This provides accurate distance measurements accounting for the Earth's curvature.
   * 
   * Formula accounts for:
   * - Earth's spherical shape (uses radius of 6371 km)
   * - Latitude/longitude coordinate system
   * - Great-circle path (shortest distance on sphere surface)
   * 
   * @param lat1 - Latitude of first point (degrees)
   * @param lon1 - Longitude of first point (degrees)
   * @param lat2 - Latitude of second point (degrees)
   * @param lon2 - Longitude of second point (degrees)
   * @returns number - Distance in kilometers
   */
  static haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.degreesToRadians(lat2 - lat1);
    const dLon = this.degreesToRadians(lon2 - lon1);
    
    // Haversine formula calculation
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.degreesToRadians(lat1)) * Math.cos(this.degreesToRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return distance;
  }

  /**
   * Utility function to convert degrees to radians for trigonometric calculations.
   * Required by Haversine formula which uses radian-based trigonometry.
   * 
   * @param degrees - Angle in degrees
   * @returns number - Angle in radians
   */
  static degreesToRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Gets contractor's job title using the new job title system with fallbacks.
   * Prioritizes user_job_title.job_title over legacy fields.
   * 
   * @param profile - Contractor's profile data
   * @returns string - Job title or empty string if none available
   */
  private static getContractorJobTitle(profile: any): string {
    // First check user_job_title (new system)
    if (profile?.user_job_title?.job_title) {
      return profile.user_job_title.job_title;
    }
    // Fallback to legacy job_title field
    if (profile?.job_title) {
      return profile.job_title;
    }
    // Final fallback to occupation field (old system)
    if (profile?.occupation) {
      return profile.occupation;
    }
    return "";
  }

  /**
   * Parses project support_type from PostgreSQL array format to string array.
   * Used for logging and debugging support type comparisons.
   * 
   * @param supportType - Support type from project (string or array)
   * @returns string[] - Array of support type values
   */
  private static parseProjectSupportTypes(supportType: any): string[] {
    if (!supportType) return [];
    
    try {
      if (typeof supportType === 'string') {
        if (supportType.startsWith('{') && supportType.endsWith('}')) {
          // PostgreSQL array format: {"Personal Support Worker (PSW)","Registered Nurse (RN)"}
          const cleanedString = supportType.slice(1, -1); // Remove { }
          return cleanedString
            .split('","')
            .map(item => item.replace(/^"|"$/g, '')); // Remove quotes
        } else {
          // Single value
          return [supportType];
        }
      } else if (Array.isArray(supportType)) {
        // Already an array
        return supportType;
      }
    } catch (error) {
      console.log('Error parsing support_type for logging:', error);
    }
    
    return [];
  }

  /**
   * Retrieves full URL for contractor avatar image from storage filename.
   * Handles the conversion from database filename to accessible URL.
   * 
   * @param avatarFilename - Filename stored in user.avatar field
   * @returns Promise<string> - Full URL to avatar image or empty string if unavailable
   */
  static async getAvatarUrl(avatarFilename?: string): Promise<string> {
    if (!avatarFilename) {
      return "";
    }

    try {
      const avatarUrl = await storage.get(avatarFilename);
      return avatarUrl;
    } catch (error) {
      console.log(`Error getting avatar URL for ${avatarFilename}:`, error);
      return "";
    }
  }

  /**
   * Transforms raw contractor database data into UI-friendly SmartMatchResult format.
   * Handles data extraction, formatting, and enrichment for display in the Smart Match modal.
   * 
   * Transformations performed:
   * - Combines first/last name into display name
   * - Calculates average rating from rating history
   * - Formats hourly rate display with currency
   * - Resolves avatar filename to full URL
   * - Calculates distance to project location
   * - Extracts and flattens skill/language arrays
   * - Determines vehicle availability from transportation methods
   * - Counts previous project engagements
   * 
   * @param contractor - Raw contractor data from GraphQL query
   * @param matchPercentage - Calculated match score (0-100)
   * @param project - Project data for distance calculation (optional)
   * @param config - Configuration for distance unit preference (optional)
   * @returns Promise<SmartMatchResult> - UI-formatted contractor data
   */
  static async transformToSmartMatchResult(
    contractor: ContractorResult,
    matchPercentage: number,
    project?: Project,
    config?: SmartMatchConfig
  ): Promise<SmartMatchResult> {
    try {
      const user = contractor.user;
      const profile = user?.profile;

      // Resolve avatar filename to accessible URL
      const avatarUrl = await this.getAvatarUrl(user?.avatar);

      // Calculate distance to project location if available
      let distance = '0 km away';
      if (project?.address && profile) {
        const unit = config?.location_radius_unit || 'km';
        distance = await this.calculateDistance(
          {
            address: profile.address,
            city: profile.city,
            state: profile.state,
            postal_code: profile.postal_code,
            country: profile.country
          },
          project.address,
          unit
        );
      }

      // Format hourly rate for display
      let hourlyRate = "Rate not set";
      if (profile?.min_rate && profile?.max_rate) {
        const currency = profile.currency || "$";
        hourlyRate = `${currency}${profile.min_rate}-${profile.max_rate}/hour`;
      } else if (profile?.max_rate) {
        const currency = profile.currency || "$";
        hourlyRate = `${currency}${profile.max_rate}/hour`;
      }

      return {
        id: contractor.id,
        userId: user.id,
        name: `${user?.given_name || ""} ${user?.family_name || ""}`.trim(),
        title: this.getContractorJobTitle(profile) || "Job title not set",
        rating:
          contractor.contractor_ratings.length > 0
            ? this.calculateAverageRating(contractor.contractor_ratings)
            : 0,
        matchPercentage,
        hourlyRate,
        avatar: avatarUrl,
        favorited: (user?.favorited_by_users?.length || 0) > 0,
        favoriteId: user?.favorited_by_users?.[0]?.id,
        bookmarked: false, // Updated later from database bookmark records
        languages: profile?.languages?.map((ul) => ul.language.name) || [],
        distance: distance,
        hasVehicle:
          profile?.transportation?.some(
            (ut) => ut.transportation_type === "driving"
          ) || false,
        availableHours: profile?.max_available_hours || 0,
        clientWorkCount: contractor.projects_aggregate?.aggregate?.count || 0,
        credentials: [], // Placeholder - would require separate credentials table
        skills: profile?.skills?.map((us) => us.skill.name) || [],
        softSkills:
          profile?.user_soft_skills?.map((uss) => uss.soft_skill.name) || [],
        certifications: user?.certifications?.map((uc) => uc.title) || [],
      };
    } catch (error) {
      console.log("Error in transformToSmartMatchResult:", error, {
        contractor,
        matchPercentage,
      });
      throw error;
    }
  }

  /**
   * Main processing pipeline for Smart Match results.
   * Coordinates the entire matching workflow from raw contractor data to sorted, UI-ready results.
   *
   * Workflow:
   * 1. Calculate match percentage for each contractor (async due to distance calculations)
   * 2. Transform contractor data to UI format
   * 3. Sort results by match percentage (highest first)
   *
   * Note: DNU (Do Not Use) filtering is handled within the match calculation,
   * not as a separate pre-filter step.
   *
   * @param contractors - Array of contractor records from GraphQL query
   * @param project - Project requirements and details
   * @param config - Manager's Smart Match configuration settings
   * @param isNewClient - Whether this is for a new client (affects scoring)
   * @param isVirtual - Whether this is a virtual visit (affects location-based scoring)
   * @returns Promise<SmartMatchResult[]> - Sorted array of UI-ready contractor matches
   */
  static async processSmartMatchResults(
    contractors: ContractorResult[],
    project: Project,
    config: SmartMatchConfig,
    isNewClient = false,
    isVirtual = false
  ): Promise<SmartMatchResult[]> {
    // Process all contractors in parallel for better performance

      // Filter out contractors with DNU_flag set to true
    // const filteredContractors = contractors.filter((contractor) => {
    //   const dnuFlag = contractor.user.profile?.DNU_flag;
    //   if (dnuFlag === true) {
    //     return false;
    //   }
    //   return true;
    // });

    const results = await Promise.all(
      contractors.map(async (contractor) => {
        // Calculate match percentage (async due to distance calculations)
        const matchPercentage = await this.calculateMatchPercentage(
          contractor,
          project,
          config,
          isNewClient,
          isVirtual
        );
        // Transform to UI format with enriched data
        return await this.transformToSmartMatchResult(
          contractor,
          matchPercentage,
          project,
          config
        );
      })
    );

    // Sort by match percentage (highest matches first)
    return results.sort((a, b) => b.matchPercentage - a.matchPercentage);
  }
}
